import { useCallback, useEffect, useRef, useState } from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { DeviceIDPatch } from "@/services/scim/profile/DeviceIDPatch";
import {
  clearGoogleHealthcareAccessToken,
  GOOGLE_HEALTHCARE_SCOPE_STRING,
} from "@/services/google/health";

declare global {
  interface Window {
    tokenClientHealthcare: any;
    googlePopupHealthcareTimeout: NodeJS.Timeout;
  }
}

// ----- Fluxo mobile (WebView do app React Native) -----
// Dentro da WebView, o popup do Google é bloqueado (disallowed_useragent). Por
// isso o OAuth é aberto no navegador do sistema via ponte com o app. Esse fluxo
// usa PKCE (sem client_secret) e um redirect por deep link do app.
// Reutiliza o MESMO client mobile do login de Agenda (Android/iOS com PKCE).
const GOOGLE_MOBILE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID_MOBILE ||
  import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GOOGLE_MOBILE_REDIRECT_URI =
  import.meta.env.VITE_GOOGLE_MOBILE_REDIRECT_URI || "fluxmed://oauth2redirect";

/** Codifica bytes em base64url (sem '+', '/' e '='), formato exigido pelo PKCE. */
function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Gera o par PKCE: um verifier aleatório e o seu challenge (SHA-256). */
async function generatePkcePair(): Promise<{
  verifier: string;
  challenge: string;
}> {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  const verifier = base64UrlEncode(randomBytes.buffer);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  const challenge = base64UrlEncode(digest);
  return { verifier, challenge };
}

/** Detecta se a aplicação está rodando dentro da WebView do app mobile. */
function isReactNativeWebView(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean((window as any).ReactNativeWebView)
  );
}

interface UseGoogleHealthcareAuthReturn {
  isConnected: boolean;
  isLoading: boolean;
  accessToken: string | null;
  handleLogin: () => void;
  handleLogout: () => Promise<void>;
}

export function useGoogleHealthcareAuth(): UseGoogleHealthcareAuthReturn {
  const { user, fetchUser } = useSessionContext();
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem("googleHealthcareAccessToken")
  );
  // Guarda o code_verifier do PKCE entre o início do login (mobile) e a volta do code.
  const codeVerifierRef = useRef<string>("");
  const googleHealthcareClientId = import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const googleHealthcareClientSecret = import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_SECRET || import.meta.env.VITE_GOOGLE_CLIENT_SECRET;
  const googleRedirectUri = window.location.origin;
  const isConnected = Boolean(user?.refreshTokenGoogleFit);

  /**
   * Persiste os tokens do Google Fit: salva o refresh token no usuário e o access
   * token no localStorage. Compartilhado pelos fluxos web (popup) e mobile.
   */
  const persistFitTokens = useCallback(
    async (tokens: any) => {
      if (!tokens?.refresh_token) {
        throw new Error("Refresh token Google Fit não recebido");
      }

      await DeviceIDPatch({
        idUser: user?.id,
        operation: "replace",
        pathName:
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_fit_refresh_token",
        valueField: tokens.refresh_token,
      });

      localStorage.setItem("googleHealthcareAccessToken", tokens.access_token);
      // Sinaliza para o dashboard reabrir o modal após vínculo bem-sucedido.
      localStorage.setItem("reopenFitnessModalAfterLink", "true");
      setAccessToken(tokens.access_token);
      await fetchUser();
    },
    [fetchUser, user?.id]
  );

  const handleTokenResponse = useCallback(
    async (response: any) => {
      try {
        if (!response?.code) return;

        const tokenResponse = await fetch("https://oauth2.googleapis.com/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            client_id: googleHealthcareClientId,
            client_secret: googleHealthcareClientSecret,
            code: response.code,
            grant_type: "authorization_code",
            access_type: "offline",
            redirect_uri: googleRedirectUri,
            scope: GOOGLE_HEALTHCARE_SCOPE_STRING,
          }),
        });

        if (!tokenResponse.ok) {
          throw new Error("Falha ao obter tokens Google Fit");
        }

        const tokens = await tokenResponse.json();
        await persistFitTokens(tokens);
      } catch (error) {
        console.error("Erro ao autenticar Google Fit:", error);
        alert("Não foi possível conectar ao Google Fit.");
      } finally {
        setIsLoading(false);
      }
    },
    [
      persistFitTokens,
      googleHealthcareClientId,
      googleHealthcareClientSecret,
      googleRedirectUri,
    ]
  );

  /**
   * Troca o `code` recebido do app (via WebView) por tokens usando PKCE.
   * Clientes OAuth mobile (Android/iOS) são públicos: NÃO usam client_secret.
   */
  const exchangeMobileCode = useCallback(
    async (code: string) => {
      try {
        const tokenResponse = await fetch(
          "https://oauth2.googleapis.com/token",
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              client_id: GOOGLE_MOBILE_CLIENT_ID,
              code,
              code_verifier: codeVerifierRef.current,
              grant_type: "authorization_code",
              redirect_uri: GOOGLE_MOBILE_REDIRECT_URI,
            }),
          }
        );

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json();
          console.error(
            "Erro ao trocar código por tokens Google Fit (mobile):",
            errorData
          );
          throw new Error("Falha ao obter tokens Google Fit (mobile)");
        }

        const tokens = await tokenResponse.json();
        await persistFitTokens(tokens);
      } catch (error) {
        console.error("Erro ao autenticar Google Fit (mobile):", error);
      } finally {
        // Limpa o verifier para não reagir a mensagens de outros fluxos depois.
        codeVerifierRef.current = "";
        setIsLoading(false);
      }
    },
    [persistFitTokens]
  );

  const initializeGoogleClient = useCallback(() => {
    if (!window.google) return;

    window.tokenClientHealthcare = window.google.accounts.oauth2.initCodeClient({
      client_id: googleHealthcareClientId,
      scope: GOOGLE_HEALTHCARE_SCOPE_STRING,
      callback: (response: any) => {
        if (window.googlePopupHealthcareTimeout) {
          clearTimeout(window.googlePopupHealthcareTimeout);
        }
        handleTokenResponse(response);
      },
      access_type: "offline",
      prompt: "consent",
      include_granted_scopes: false,
      select_account: false,
      ux_mode: "popup",
      redirect_uri: googleRedirectUri,
    });
  }, [googleHealthcareClientId, googleRedirectUri, handleTokenResponse]);

  useEffect(() => {
    const gsiScript = document.createElement("script");
    gsiScript.src = "https://accounts.google.com/gsi/client";
    gsiScript.async = true;
    gsiScript.defer = true;
    gsiScript.onload = initializeGoogleClient;
    document.head.appendChild(gsiScript);

    const handleGoogleMessage = (event: MessageEvent) => {
      if (
        event.origin.includes("accounts.google.com") &&
        event.data &&
        (event.data.type === "cancel" || event.data.type === "close")
      ) {
        setIsLoading(false);
      }
    };

    window.addEventListener("message", handleGoogleMessage);

    return () => {
      window.removeEventListener("message", handleGoogleMessage);
      gsiScript.remove();
    };
  }, [initializeGoogleClient]);

  const handleLogin = useCallback(async () => {
    // Dentro da WebView do app mobile: abre o OAuth no navegador do sistema
    // (via ponte com o React Native), pois o Google bloqueia OAuth em WebView.
    if (isReactNativeWebView()) {
      try {
        setIsLoading(true);

        // Gera o par PKCE e guarda o verifier para a troca do code depois.
        const { verifier, challenge } = await generatePkcePair();
        codeVerifierRef.current = verifier;

        // Monta a URL de consentimento do Google usando os escopos do Fit.
        const authUrl =
          "https://accounts.google.com/o/oauth2/v2/auth?" +
          new URLSearchParams({
            client_id: GOOGLE_MOBILE_CLIENT_ID,
            redirect_uri: GOOGLE_MOBILE_REDIRECT_URI,
            response_type: "code",
            access_type: "offline",
            prompt: "consent",
            code_challenge: challenge,
            code_challenge_method: "S256",
            scope: GOOGLE_HEALTHCARE_SCOPE_STRING,
          }).toString();

        // Pede ao app para abrir a URL no navegador do sistema.
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "GOOGLE_OAUTH",
            payload: { url: authUrl, redirectUri: GOOGLE_MOBILE_REDIRECT_URI },
          })
        );
      } catch (error) {
        console.error("Erro ao iniciar login Google Fit (mobile):", error);
        setIsLoading(false);
      }
      return;
    }

    // Fluxo web padrão (popup do Google Identity Services).
    if (!window.tokenClientHealthcare) return;

    setIsLoading(true);
    window.googlePopupHealthcareTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 60000);
    window.tokenClientHealthcare.requestCode();
  }, []);

  /**
   * Escuta as mensagens vindas do app mobile (via WebView) com o resultado do
   * OAuth do Google Fit aberto no navegador do sistema: o `code` (sucesso) ou
   * um erro/cancelamento. O app envia via injectJavaScript (MessageEvent string).
   *
   * Obs.: o login de Agenda também escuta 'GOOGLE_OAUTH_CODE'. Como o app envia
   * a mesma mensagem, cada hook só age quando ele foi quem iniciou o fluxo
   * (guardou um code_verifier); por isso checamos codeVerifierRef antes de trocar.
   */
  useEffect(() => {
    const handleNativeAuthMessage = (event: MessageEvent) => {
      let data: any = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return; // Não é uma mensagem JSON nossa.
        }
      }
      if (!data || typeof data !== "object") return;

      if (data.type === "GOOGLE_OAUTH_CODE" && data.payload?.code) {
        // Só troca o code se este hook iniciou o fluxo (tem verifier salvo).
        if (!codeVerifierRef.current) return;
        exchangeMobileCode(data.payload.code);
      } else if (data.type === "GOOGLE_OAUTH_ERROR") {
        // Só reage ao erro se este hook iniciou o fluxo.
        if (!codeVerifierRef.current) return;
        codeVerifierRef.current = "";
        setIsLoading(false);
        if (data.payload?.message && data.payload.message !== "cancelled") {
          console.warn(
            "Login Google Fit (mobile) falhou:",
            data.payload.message
          );
        }
      }
    };

    window.addEventListener("message", handleNativeAuthMessage);
    return () =>
      window.removeEventListener("message", handleNativeAuthMessage);
  }, [exchangeMobileCode]);

  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      await DeviceIDPatch({
        idUser: user?.id,
        operation: "replace",
        pathName:
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_fit_refresh_token",
        valueField: "",
      });
      clearGoogleHealthcareAccessToken();
      setAccessToken(null);
      await fetchUser();
    } catch (error) {
      console.error("Erro ao desconectar Google Fit:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUser, user?.id]);

  return {
    isConnected,
    isLoading,
    accessToken,
    handleLogin,
    handleLogout,
  };
}
